import React, {
  useState, useEffect, useCallback, useMemo, Suspense,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useApolloClient } from '@apollo/client'
import { ErrorBoundary } from 'react-error-boundary'
import { connect as connectStaticText } from '../../../../../StaticText/connect'
import {
  StaticTextProvider,
  useTranslatedText,
  InternationalStrings,
} from '../../../../../StaticText/StaticTextContext'
import { selectAuth } from '../../../../../../services/auth/selectors'
import Plus from '../../../../../Icon/icons/Plus/index'
import Button, { TYPES as BUTTON_TYPES, SIZES as BUTTON_SIZES } from '../../../../../Button/index'
import CommunityTabBar from '../../../shared/CommunityTabBar/index'
import CommunityErrorFallback from '../../../../shared/ErrorHandling/CommunityErrorFallback/index'
import usePlaylistFollowMutations from '../../../../../../services/community/identity/library/usePlaylistFollowMutations'
import useDiscoverTabState from '../../../../../../services/community/identity/library/useDiscoverTabState'
import type useCollectionsData from '../../../../../../services/community/identity/library/useCollectionsData'
import showSaveErrorToast from '../../../../../../services/community/shared/save-error-toast'
import { addToasty } from '../../../../../../services/toasty/actions'
import ProfileFollowingTab from '../FollowingTab/index'
import ProfileDiscoverTab from '../DiscoverTab/index'
import ProfileMyPlaylists from '../MyPlaylistsTab/index'
import { computeMyPlaylistsRowCount } from '../MyPlaylistsTab/myPlaylistsRowCount'
import type { FieldVisibility } from '../../../../../../graphql-types'
import type {
  PlaylistShell,
  FollowedPlaylistShellNode,
  PlaylistVisibilityLevel,
  OnActivityVisibility,
} from '../../../../../../services/community/types'
import usePlaylistTabHandlers from './usePlaylistTabHandlers'
import PlaylistTabsHeader from './PlaylistTabsHeader'
import PlaylistTabsSearchBar from './PlaylistTabsSearchBar'

const CreatePlaylistModal = React.lazy(() => import('../../../../Playlist/shared/CreatePlaylistModal/index'))
const preloadCreatePlaylistModal = () => { import('../../../../Playlist/shared/CreatePlaylistModal/index') }

export const TABS = {
  MY_PLAYLISTS: 'myPlaylists',
  FOLLOWING: 'following',
  DISCOVER: 'discover',
} as const

export type CollectionsTab = typeof TABS[keyof typeof TABS]

// ---------------------------------------------------------------------------
// Props — slice-typed. The five slices come from `useCollectionsData`'s
// sliced return shape (myPlaylists / followed / watchHistory / items /
// search). Each slice is a single identity-stable reference so a broadcast
// to one domain leaves the other four slice props identity-stable;
// downstream React.memo compares hold on those.
//
// Page-only props (loggedInUuid, watchHistoryVisibility, etc.) come from
// outside `collectionsData` and are passed directly by CommunityMyLibraryPage.
// ---------------------------------------------------------------------------

// Derive slice types from the hook's return shape. Single source of truth —
// changing the hook's return automatically reshapes these props.
type CollectionsData = ReturnType<typeof useCollectionsData>
export type MyPlaylistsSlice = CollectionsData['myPlaylists']
export type FollowedSlice = CollectionsData['followed']
export type WatchHistorySlice = CollectionsData['watchHistory']
export type ItemsSlice = CollectionsData['items']
export type SearchSlice = CollectionsData['search']

export interface PlaylistTabContainerProps {
  // Sliced data from useCollectionsData (each is a memoized identity-stable
  // reference; per-slice React.memo on consumers catches the no-change case).
  myPlaylists: MyPlaylistsSlice
  followed: FollowedSlice
  watchHistory: WatchHistorySlice
  items: ItemsSlice
  search: SearchSlice

  // Shared (top-level on useCollectionsData's return)
  collectionsTab: CollectionsTab
  onCollectionsTabChange: (tab: CollectionsTab) => void
  isPreview?: boolean
  isProfilePrivate?: boolean
  language?: string
  hoursWatched?: number
  authorUsername: string
  authorUid?: string
  seeMoreLabel: string
  showLessLabel: string

  // Page-only — passed directly by CommunityMyLibraryPage, not via
  // collectionsData spread.
  /** UserProfile.watchHistoryVisibility flag. */
  watchHistoryVisibility?: FieldVisibility | null
  /** UserProfile.currentlyWatchingVisibility flag. */
  currentlyWatchingVisibility?: FieldVisibility | null
  /** Owner-side mutation handler for the activity-flag fields. */
  onActivityVisibility?: OnActivityVisibility | null
  /** Logged-in user's UUID — forwarded to ProfileMyPlaylists so the
   *  private-profile banner can link its "View your Profile" CTA to the
   *  user's own profile page. */
  loggedInUuid?: string
  wrapInCard?: boolean

  staticText: InternationalStrings
}

type PlaylistTabContainerContentProps = Omit<PlaylistTabContainerProps, 'staticText'>

function PlaylistTabContainerInner ({
  myPlaylists,
  followed,
  watchHistory,
  items,
  search,
  collectionsTab,
  onCollectionsTabChange,
  isPreview = false,
  language = 'en',
  // authorUsername + authorUid stay on the prop interface for API parity
  // (the library page passes them) but the My Playlists tab implies the
  // owner and never renders the author line.
  // watchHistoryVisibility / currentlyWatchingVisibility / onActivityVisibility
  // stay on the prop interface for API parity — sharing was removed from
  // the four system playlists, so these props are no longer threaded into
  // the My Playlists tab.
  seeMoreLabel,
  showLessLabel,
  wrapInCard = false,
  isProfilePrivate = false,
  loggedInUuid,
}: PlaylistTabContainerContentProps) {
  // Destructure each slice into locals. The slice references are
  // identity-stable across unrelated Apollo broadcasts, so when only (e.g.)
  // `watchHistory` changes, the other four slices keep their identity.
  const {
    allUserPlaylistsJS,
    playlistsLoading,
    sharedOnProfileCount,
    allowedVisibilities,
    onUpdatePlaylistVisibility,
    onPlaylistAction,
    onRemovePlaylistItem,
    onReorderPlaylistItem,
    refetchPlaylists,
  } = myPlaylists
  const {
    followedPlaylistsJS,
    followedPlaylistsTotalCount,
    followedPlaylistsLoading,
    onRemoveFollowedPlaylist,
    onReFollowPlaylist,
    refetchFollowedPlaylists,
    loadMoreFollowedPlaylists,
    hasMoreFollowedPlaylists,
    isLoadingMoreFollowedPlaylists,
  } = followed
  const {
    inProgressItems,
    inProgressIsHydrating,
    inProgressOverallProgress,
    watchHistoryItems,
    watchHistoryTotalCount,
    watchHistoryTotalDuration,
    onRemoveWatchHistoryItem,
    onHideInProgressItem,
  } = watchHistory
  const {
    watchLaterPlaylist,
    favoritesPlaylist,
    onRemoveWatchLaterItem,
    onReorderWatchLaterItem,
    onRemoveFavoritesItem,
    onReorderFavoritesItem,
  } = items
  const {
    collectionsSearch,
    onCollectionsSearch,
    searchResultIds,
  } = search
  const auth = useSelector(selectAuth)
  const jwt = auth.get('jwt')
  const apolloClient = useApolloClient()
  const dispatch = useDispatch()

  // Title bar and tab/search labels — pulled via the static-text hook from
  // the `profileCollectionsTabs` slice (StaticTextProvider in the outer
  // wrapper).
  const collectionsTitle = useTranslatedText({ keys: ['collectionsTitle'] })
  const sharingCountTemplate = useTranslatedText({ keys: ['sharingCount'] })
  const allPrivateLabel = useTranslatedText({ keys: ['allPrivate'] })
  const createPlaylistLabel = useTranslatedText({ keys: ['createPlaylist'] })
  const stMyPlaylistsTab = useTranslatedText({ keys: ['myPlaylistsTab'] })
  const stFollowingTab = useTranslatedText({ keys: ['followingTab'] })
  const stDiscoverTab = useTranslatedText({ keys: ['discoverTab'] })
  const stSearchMyPlaylists = useTranslatedText({ keys: ['searchMyPlaylists'] })
  const stSearchFollowing = useTranslatedText({ keys: ['searchFollowing'] })
  const stSearchDiscover = useTranslatedText({ keys: ['searchDiscover'] })
  const saveErrorMessage = useTranslatedText({ keys: ['saveError'] })
  const autoRenamedNoticeTemplate = useTranslatedText({ keys: ['autoRenamedNotice'] })

  const handleMutationError = useCallback(() => {
    showSaveErrorToast(dispatch, saveErrorMessage)
  }, [dispatch, saveErrorMessage])

  const [showCreateModal, setShowCreateModal] = useState(false)
  const handleOpenCreateModal = useCallback(() => {
    setShowCreateModal(true)
  }, [])
  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false)
  }, [])
  const [searchTrigger, setSearchTrigger] = useState(0)

  // ── Keep-alive tabs ─────────────────────────────────────────────────
  // Sticky latch over tabs: once visited, a tab subtree stays mounted
  // with the native `hidden` attribute hiding it instead of unmounting.
  // This stops per-row `useQuery(GET_CONTENT_FOR_TILE_HYDRATION)`
  // instances from tearing down and re-firing on every tab toggle —
  // `cache-and-network` (load-bearing for `userMediaPosition`) was
  // refetching on every remount before this change. Mirrors the
  // `followingEverActive` latch in `useCollectionsData` and the lifted
  // `useDiscoverTabState` pattern already in this file.
  const [activatedTabs, setActivatedTabs] = useState<Set<CollectionsTab>>(
    () => new Set([collectionsTab]),
  )
  useEffect(() => {
    setActivatedTabs((prev) => (
      prev.has(collectionsTab) ? prev : new Set(prev).add(collectionsTab)
    ))
  }, [collectionsTab])

  // Idle pre-mount of FOLLOWING once My Playlists has settled. Pairs
  // with the `followingEverActive` shell-query stagger in
  // useCollectionsData (which pre-warms the followed-shells query on
  // the same signal); here we additionally mount the subtree so its
  // per-row hydration completes during idle time. First user click on
  // FOLLOWING is then instant (no `CommunityContentForTileHydration`
  // round-trip). DISCOVER stays lazy — it doesn't auto-fetch anything
  // until the user types a query, so pre-mounting it has no benefit.
  const myPlaylistsSettled = !playlistsLoading && !inProgressIsHydrating
  useEffect(() => {
    if (!myPlaylistsSettled) return undefined
    if (activatedTabs.has(TABS.FOLLOWING)) return undefined
    if (typeof window === 'undefined') return undefined
    const supportsIdle = 'requestIdleCallback' in window
    const activate = () => {
      setActivatedTabs((prev) => (
        prev.has(TABS.FOLLOWING) ? prev : new Set(prev).add(TABS.FOLLOWING)
      ))
    }
    if (supportsIdle) {
      const handle = window.requestIdleCallback(activate)
      return () => window.cancelIdleCallback(handle)
    }
    // Safari < 16.4 fallback — schedule on the next macrotask so we
    // don't contend with main-thread work right after settle.
    const timeoutHandle = window.setTimeout(activate, 200)
    return () => window.clearTimeout(timeoutHandle)
  }, [myPlaylistsSettled, activatedTabs])

  // Lifted Discover tab state — lives at container level so results persist
  // across tab switches (children mount/unmount; the container does not).
  const discover = useDiscoverTabState(
    jwt,
    language,
    collectionsSearch,
    searchTrigger,
    collectionsTab === TABS.DISCOVER,
  )

  const {
    followedIds,
    followerCounts,
    unfollowToast,
    handleFollowPlaylist,
    handleUnfollowDiscover,
    handleUndoDiscoverUnfollow,
    handleDismissDiscoverUnfollow,
    handleUnfollowFollowing,
    flushPendingUnfollow,
    setUnfollowToast,
  } = usePlaylistFollowMutations({
    jwt,
    apolloClient,
    followedPlaylists: followedPlaylistsJS,
    activeResults: discover.activeResults,
    removeFollowedPlaylist: onRemoveFollowedPlaylist,
    refetchFollowedPlaylists,
    onMutationError: handleMutationError,
  })

  // Six inline useCallbacks + useTransition + two PostHog hooks
  // consolidated behind one call.
  const handlers = usePlaylistTabHandlers({
    collectionsTab,
    onCollectionsTabChange,
    onCollectionsSearch,
    setSearchTrigger,
    flushPendingUnfollow,
    setUnfollowToast,
    refetchPlaylists,
  })

  // Tab badge = number of CUSTOM (user-authored) playlists only.  System
  // rows (Watch Later, Favorites, Watch History, Currently Watching) are
  // always-on / data-driven presence rows; counting them inflates the badge
  // by a constant and confuses the owner who reads "My Playlists" as
  // "playlists I made".
  const myPlaylistsBadge = allUserPlaylistsJS.length

  // Empty-state gate (search input visibility): does ANY row render?  Keeps
  // the SearchInput visible while system rows exist even when the user has
  // zero custom playlists.  See `myPlaylistsRowCount.ts` for the
  // source-of-truth list of owner-only system rows.
  const myPlaylistsHasRows = computeMyPlaylistsRowCount({
    customPlaylistsCount: allUserPlaylistsJS.length,
    hasInProgressItems: inProgressItems.length > 0,
    isPreview,
  }) > 0

  const tabs = useMemo(() => [
    {
      key: TABS.MY_PLAYLISTS,
      label: stMyPlaylistsTab,
      count: myPlaylistsBadge,
    },
    {
      key: TABS.FOLLOWING,
      label: stFollowingTab,
      count: followedPlaylistsTotalCount != null
        ? followedPlaylistsTotalCount
        : followedPlaylistsJS.length || null,
    },
    {
      key: TABS.DISCOVER,
      label: stDiscoverTab,
      count: discover.activeResults.playlists.length || null,
    },
  ], [
    stMyPlaylistsTab, stFollowingTab, stDiscoverTab,
    myPlaylistsBadge, followedPlaylistsTotalCount, followedPlaylistsJS.length,
    discover.activeResults.playlists.length,
  ])

  const placeholders = useMemo(() => ({
    myPlaylists: stSearchMyPlaylists,
    following: stSearchFollowing,
    discover: stSearchDiscover,
  }), [stSearchMyPlaylists, stSearchFollowing, stSearchDiscover])

  // Existing playlist names list passed to CreatePlaylistModal for the
  // auto-rename collision check.  Memoize to keep the prop reference stable
  // across container renders — the modal reads this on every keystroke
  // while open, and a fresh array per render would defeat the memo on
  // any future React.memo wrapping the modal.
  const existingPlaylistNames = useMemo(
    () => allUserPlaylistsJS
      .map((p) => p?.playlistName)
      .filter((n): n is string => Boolean(n)),
    [allUserPlaylistsJS],
  )

  // Active-tab hasRows derivation: hides the search input when the active
  // tab has no rows (empty-state CTA would otherwise compete for vertical
  // space).
  const activeTabHasRows = (
    (collectionsTab === TABS.FOLLOWING
      && (followedPlaylistsJS.length > 0
        || (followedPlaylistsTotalCount != null && followedPlaylistsTotalCount > 0)))
    || (collectionsTab === TABS.MY_PLAYLISTS && myPlaylistsHasRows)
    || collectionsTab === TABS.DISCOVER
  )

  // ── Loading guard ────────────────────────────────────────────────────
  // `playlistsLoading` here is the slice's "we have nothing to show yet"
  // signal (defined in useProfilePlaylists as `shellLoading && !playlistsRawData`).
  // Under cache-and-network, Apollo's raw `shellLoading` flips true on
  // every revisit while the background revalidation runs, but the cached
  // playlistsRawData is already populated — so `playlistsLoading` is false
  // on revisit and this guard does not blank the tabs.  Gating on the raw
  // `shellLoading` here would force a flash-of-empty on every return.
  if (playlistsLoading) return null

  // Active-tab booleans drive the tabpanel className/aria-hidden/inert
  // triple below.  See the keep-alive comment above the
  // `playlist-tab-container__content` element for why we hide inactive
  // panels with CSS (`--inactive`) rather than the native `hidden`
  // attribute.
  const isMyPlaylistsActive = collectionsTab === TABS.MY_PLAYLISTS
  const isFollowingActive = collectionsTab === TABS.FOLLOWING
  const isDiscoverActive = collectionsTab === TABS.DISCOVER

  const content = (
    <div className="playlist-tab-container">
      <div className="playlist-tab-container__sticky-controls">
        <PlaylistTabsHeader
          collectionsTitle={collectionsTitle}
          sharingCountTemplate={sharingCountTemplate}
          sharedOnProfileCount={sharedOnProfileCount}
          allPrivateLabel={allPrivateLabel}
          createPlaylistLabel={createPlaylistLabel}
          showCreate
          onCreateClick={handleOpenCreateModal}
          onCreatePreload={preloadCreatePlaylistModal}
        />

        <CommunityTabBar<CollectionsTab>
          tabs={tabs}
          activeTab={collectionsTab}
          onTabChange={handlers.handleTabClick}
          className="playlist-tab-container__tab-bar"
        />

        <PlaylistTabsSearchBar
          collectionsTab={collectionsTab}
          collectionsSearch={collectionsSearch}
          onSearchChange={handlers.handleSearchChange}
          onSearchSubmit={handlers.handleSearchSubmit}
          placeholders={placeholders}
          hasRows={activeTabHasRows}
        />

        {/* Mobile create button stays as a SIBLING of the header — moving
            it into <PlaylistTabsHeader> would break sticky positioning and
            the __create-btn--mobile CSS targeting. */}
        <Button
          type={BUTTON_TYPES.PRIMARY}
          size={BUTTON_SIZES.DEFAULT}
          className="playlist-tab-container__create-btn--mobile"
          onClick={handleOpenCreateModal}
          onMouseEnter={preloadCreatePlaylistModal}
        >
          <Plus />
          {createPlaylistLabel}
        </Button>
      </div>

      {/*
        Keep-alive tabs. Each tab subtree is mounted on first activation
        (initial render OR idle pre-mount OR user click) and stays
        mounted thereafter.

        Inactive panels are hidden with the `--inactive` CSS class
        (visibility:hidden + position:absolute over the active panel),
        NOT the native `hidden` attribute. `[hidden]` applies
        `display:none`, which zeroes out the `offsetWidth` measurements
        Row.v2 reads in `componentDidMount` / `componentDidUpdate` to
        compute its tile-per-page `limit`. Tabs pre-mounted under
        `display:none` (the idle pre-mount of FOLLOWING below, or any
        per-row hydration that lands while the tab is hidden) write a
        NaN `limit` into Row.v2 state and never recover — the tiles
        overflow the white row card on the next reveal. Keeping the
        subtree in layout via CSS preserves the measurement.

        a11y note: `[hidden]` removes the subtree from the a11y /
        focus tree for free; CSS hiding does not, so we add
        `aria-hidden` + `inert` on each inactive panel to recover both
        (screen-reader announcement and keyboard focusability).

        Per-tab ErrorBoundary isolates failures — an error in one tab no
        longer blanks the others. We drop the prior shared `resetKeys=
        {[collectionsTab]}` because tabs no longer remount on switch;
        recovery flows through `CommunityErrorFallback`'s retry button
        (`resetErrorBoundary`) or a page navigation.
      */}
      <div className="playlist-tab-container__content">
        {activatedTabs.has(TABS.MY_PLAYLISTS) && (
          <div
            id={`${TABS.MY_PLAYLISTS}-tabpanel`}
            role="tabpanel"
            aria-labelledby={`${TABS.MY_PLAYLISTS}-tab`}
            className={isMyPlaylistsActive
              ? 'playlist-tab-container__tabpanel'
              : 'playlist-tab-container__tabpanel playlist-tab-container__tabpanel--inactive'}
            aria-hidden={!isMyPlaylistsActive}
            inert={!isMyPlaylistsActive ? '' : undefined}
            tabIndex={-1}
          >
            <ErrorBoundary
              FallbackComponent={CommunityErrorFallback}
              onError={handlers.handleTabRenderError}
            >
              <ProfileMyPlaylists
                inProgressItems={inProgressItems}
                inProgressIsHydrating={inProgressIsHydrating}
                inProgressOverallProgress={inProgressOverallProgress}
                playlists={allUserPlaylistsJS as unknown as PlaylistShell[]}
                defaultPlaylist={watchLaterPlaylist as unknown as PlaylistShell | null}
                favoritesPlaylist={favoritesPlaylist as unknown as PlaylistShell | null}
                language={language}
                jwt={jwt}
                searchQuery={collectionsSearch}
                searchResultIds={searchResultIds}
                onUpdateVisibility={onUpdatePlaylistVisibility}
                onPlaylistAction={onPlaylistAction}
                onRemoveItem={onRemovePlaylistItem}
                onReorderItem={onReorderPlaylistItem}
                onHideInProgressItem={onHideInProgressItem}
                allowedVisibilities={
                  allowedVisibilities as readonly PlaylistVisibilityLevel[] | undefined
                }
                onRemoveWatchLaterItem={onRemoveWatchLaterItem}
                onReorderWatchLaterItem={onReorderWatchLaterItem}
                onRemoveFavoritesItem={onRemoveFavoritesItem}
                onReorderFavoritesItem={onReorderFavoritesItem}
                watchHistoryItems={watchHistoryItems}
                watchHistoryTotalCount={watchHistoryTotalCount}
                watchHistoryTotalDuration={watchHistoryTotalDuration}
                onRemoveWatchHistoryItem={onRemoveWatchHistoryItem}
                isPreview={isPreview}
                isProfilePrivate={isProfilePrivate}
                loggedInUuid={loggedInUuid}
              />
            </ErrorBoundary>
          </div>
        )}
        {activatedTabs.has(TABS.FOLLOWING) && (
          <div
            id={`${TABS.FOLLOWING}-tabpanel`}
            role="tabpanel"
            aria-labelledby={`${TABS.FOLLOWING}-tab`}
            className={isFollowingActive
              ? 'playlist-tab-container__tabpanel'
              : 'playlist-tab-container__tabpanel playlist-tab-container__tabpanel--inactive'}
            aria-hidden={!isFollowingActive}
            inert={!isFollowingActive ? '' : undefined}
            tabIndex={-1}
          >
            <ErrorBoundary
              FallbackComponent={CommunityErrorFallback}
              onError={handlers.handleTabRenderError}
            >
              <ProfileFollowingTab
                followedPlaylists={followedPlaylistsJS as unknown as FollowedPlaylistShellNode[]}
                followedPlaylistsProcessing={followedPlaylistsLoading}
                followedPlaylistsTotalCount={followedPlaylistsTotalCount}
                searchQuery={collectionsSearch}
                language={language}
                jwt={jwt}
                unfollowToast={unfollowToast}
                onUnfollow={handleUnfollowFollowing}
                onToastComplete={handlers.handleFollowingToastComplete}
                reFollowPlaylist={onReFollowPlaylist}
                removeFollowedPlaylist={onRemoveFollowedPlaylist}
                loadMoreFollowedPlaylists={loadMoreFollowedPlaylists}
                hasMoreFollowedPlaylists={hasMoreFollowedPlaylists}
                isLoadingMoreFollowedPlaylists={isLoadingMoreFollowedPlaylists}
                seeMoreLabel={seeMoreLabel}
                showLessLabel={showLessLabel}
              />
            </ErrorBoundary>
          </div>
        )}
        {activatedTabs.has(TABS.DISCOVER) && (
          <div
            id={`${TABS.DISCOVER}-tabpanel`}
            role="tabpanel"
            aria-labelledby={`${TABS.DISCOVER}-tab`}
            className={isDiscoverActive
              ? 'playlist-tab-container__tabpanel'
              : 'playlist-tab-container__tabpanel playlist-tab-container__tabpanel--inactive'}
            aria-hidden={!isDiscoverActive}
            inert={!isDiscoverActive ? '' : undefined}
            tabIndex={-1}
          >
            <ErrorBoundary
              FallbackComponent={CommunityErrorFallback}
              onError={handlers.handleTabRenderError}
            >
              <ProfileDiscoverTab
                discover={discover}
                followedIds={followedIds}
                followerCounts={followerCounts}
                unfollowToast={unfollowToast}
                onFollow={handleFollowPlaylist}
                onUnfollow={handleUnfollowDiscover}
                onUndoUnfollow={handleUndoDiscoverUnfollow}
                onDismissUnfollow={handleDismissDiscoverUnfollow}
                seeMoreLabel={seeMoreLabel}
                showLessLabel={showLessLabel}
              />
            </ErrorBoundary>
          </div>
        )}
      </div>

      {showCreateModal ? (
        <Suspense fallback={null}>
          <CreatePlaylistModal
            onClose={handleCloseCreateModal}
            onCreated={handlers.handlePlaylistCreated}
            existingPlaylistNames={existingPlaylistNames}
            onAutoRenamed={(originalName, finalName) => {
              const message = autoRenamedNoticeTemplate
                .replace('{finalName}', finalName)
                .replace('{originalName}', originalName)
              dispatch(addToasty(message))
            }}
          />
        </Suspense>
      ) : null}

    </div>
  )

  if (wrapInCard) {
    return (
      <div className="community-profile-user__card community-profile-user__card--view">
        {content}
      </div>
    )
  }

  return content
}

function PlaylistTabContainer (props: PlaylistTabContainerProps) {
  const { staticText, ...rest } = props
  return (
    <StaticTextProvider staticText={staticText}>
      <PlaylistTabContainerInner {...rest} />
    </StaticTextProvider>
  )
}

// React.memo sits inside connectStaticText so the shallow compare runs on the
// underlying component's props (which are now reference-stable thanks to the
// useMemo wrappers in useCollectionsData + usePlaylistFollowMutations) rather
// than on the HOC's injected staticText, which re-emits as a fresh Immutable
// Map reference on every Redux hydration.
export default connectStaticText({ storeKey: 'profileCollectionsTabs' })(
  React.memo(PlaylistTabContainer),
)
